import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { MapPin, Phone, Zap } from "lucide-react";
import { BUSINESS, getOpenStatus } from "./data";

const EASE = [0.16, 1, 0.3, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1550041473-d296a3a8a18a?auto=format&fit=crop&w=1200&q=80";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const status = getOpenStatus();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), {
    stiffness: 120,
    damping: 18,
  });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-24"
    >
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_70%_60%_at_50%_35%,black,transparent)]"
      />
      <div className="absolute -top-40 right-[-10%] h-[480px] w-[480px] rounded-full bg-blue-500/15 blur-[140px]" />
      <div className="absolute bottom-[-20%] left-[-10%] h-[420px] w-[420px] rounded-full bg-cyan-500/10 blur-[140px]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)]">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            data-testid="hero-badge"
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-blue-400/30 bg-blue-400/5 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] text-blue-400"
          >
            <Zap className="h-3.5 w-3.5" />
            Präzise Handy-Reparatur in Gütersloh
          </motion.p>

          <h1 className="font-display text-[clamp(2.75rem,7.5vw,5.5rem)] font-extrabold uppercase leading-[0.95] tracking-tight text-slate-50">
            <MaskedLine delay={0.25}>Präzision</MaskedLine>
            <MaskedLine delay={0.37}>
              <span className="text-blue-400">statt</span>
            </MaskedLine>
            <MaskedLine delay={0.49}>
              <span className="text-outline">Neukauf.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            data-testid="hero-subheadline"
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            Display gebrochen, Akku leer, Wasserschaden? H.Elektronik repariert
            Ihr Smartphone am Westring 37 — meist in 30–60 Minuten, mit
            hochwertigen Ersatzteilen und ehrlichem Festpreis.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#kontakt"
              data-testid="hero-request-button"
              className="rounded-full bg-blue-400 px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#07080c] transition-all duration-300 hover:bg-slate-50 hover:shadow-[0_0_40px_rgba(59,130,246,0.4)]"
            >
              Reparatur anfragen
            </a>
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-call-button"
              className="flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-200 transition-colors duration-300 hover:border-blue-400/50 hover:text-blue-400"
            >
              <Phone className="h-3.5 w-3.5" />
              {BUSINESS.phoneDisplay}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            data-testid="hero-status-bar"
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/10 pt-6 text-sm text-slate-400"
          >
            <span className="flex items-center gap-2.5">
              <MapPin className="h-4 w-4 text-blue-400" />
              Westring 37 · 33334 Gütersloh
            </span>
            <span
              data-testid="hero-open-status"
              className="flex items-center gap-2.5"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full animate-pulse-dot ${
                  status.open ? "bg-emerald-400" : "bg-blue-400"
                }`}
              />
              {status.label}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">
              N 51.9069° · O 8.3785°
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="relative"
          style={{ perspective: 1200 }}
        >
          <motion.div
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <motion.div
              style={{ y: imgY }}
              className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_120px_-20px_rgba(59,130,246,0.25)]"
            >
              <img
                src={HERO_IMG}
                alt="Techniker bei der präzisen Smartphone-Reparatur"
                data-testid="hero-image"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07080c]/75 via-transparent to-transparent" />
              <div
                data-testid="hero-image-caption"
                className="absolute bottom-5 left-5 right-5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300"
              >
                <span>Mikrolöten · Chip-Level</span>
                <span className="text-blue-400">EST. Gütersloh</span>
              </div>
            </motion.div>
            <div
              style={{ transform: "translateZ(50px)" }}
              data-testid="hero-express-chip"
              className="glass-panel absolute -left-4 top-8 rounded-xl border border-white/10 px-4 py-3 sm:-left-8"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Express-Service
              </p>
              <p className="font-display text-xl font-bold text-slate-50">
                30–60 <span className="text-blue-400">Min</span>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
